import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { chatIntro, chatQuestions } from "../constants/data";

const TypedText = ({ text, onDone }) => {
    const [shown, setShown] = useState("");

    useEffect(() => {
        let i = 0;
        const id = setInterval(() => {
            i += 3;
            setShown(text.slice(0, i));
            if (i >= text.length) {
                clearInterval(id);
                onDone && onDone();
            }
        }, 18);
        return () => clearInterval(id);
    }, [text, onDone]);

    return <>{shown}</>;
};

const AskMe = () => {
    const [thread, setThread] = useState([
        { type: "answer", text: chatIntro, done: true },
    ]);
    const [busy, setBusy] = useState(false);
    const [asked, setAsked] = useState([]);
    const scrollRef = useRef(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    });

    const ask = (q) => {
        if (busy || asked.includes(q.id)) return;
        setAsked((prev) => [...prev, q.id]);
        setThread((prev) => [...prev, { type: "question", text: q.label }]);
        setBusy(true);

        setTimeout(() => {
            setThread((prev) => [
                ...prev,
                { type: "answer", text: q.answer, done: false },
            ]);
        }, 550);
    };

    const markDone = () => {
        setThread((prev) =>
            prev.map((m, i) => (i === prev.length - 1 ? { ...m, done: true } : m))
        );
        setBusy(false);
    };

    const remaining = chatQuestions.filter((q) => !asked.includes(q.id));
    const lastMessage = thread[thread.length - 1];
    const showTypingDots =
        busy && lastMessage && lastMessage.type === "question";

    return (
        <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-elevated shadow-soft">
            <div className="flex items-center gap-3 border-b border-line px-5 py-3.5">
                <img
                    src="/assets/profile.jpeg"
                    alt="Harshit Chaunal"
                    className="h-9 w-9 rounded-full border border-line object-cover"
                />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-fg">ask.harshit</p>
                    <p className="truncate font-mono text-[10px] uppercase tracking-widest text-faint">
                        scripted agent · no tokens were harmed
                    </p>
                </div>
                <span className="flex shrink-0 items-center gap-2 rounded-full border border-line px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-muted">
                    <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-accent" />
                    online
                </span>
            </div>

            <div
                ref={scrollRef}
                className="flex min-h-[240px] flex-1 flex-col gap-3 overflow-y-auto scroll-smooth px-5 py-5 sm:min-h-[280px]"
            >
                {thread.map((m, i) =>
                    m.type === "question" ? (
                        <motion.p
                            key={i}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-fg px-4 py-2.5 text-sm text-base"
                        >
                            {m.text}
                        </motion.p>
                    ) : (
                        <motion.p
                            key={i}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="max-w-[92%] rounded-2xl rounded-bl-md bg-panel px-4 py-2.5 text-sm leading-relaxed text-fg"
                        >
                            {m.done ? m.text : <TypedText text={m.text} onDone={markDone} />}
                        </motion.p>
                    )
                )}

                <AnimatePresence>
                    {showTypingDots && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex w-fit gap-1.5 rounded-2xl rounded-bl-md bg-panel px-4 py-3"
                        >
                            <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted" />
                            <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted" />
                            <span className="typing-dot h-1.5 w-1.5 rounded-full bg-muted" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <div className="border-t border-line px-4 py-3.5 sm:px-5">
                {remaining.length > 0 ? (
                    <>
                        <p className="mb-2.5 font-mono text-[10px] uppercase tracking-widest text-faint">
                            Try a prompt · {remaining.length} left
                        </p>
                        <div className="flex max-h-[88px] flex-wrap gap-2 overflow-y-auto">
                            {remaining.map((q) => (
                                <button
                                    key={q.id}
                                    onClick={() => ask(q)}
                                    disabled={busy}
                                    className="rounded-full border border-line bg-elevated px-3 py-1.5 text-[12px] text-muted transition-all hover:border-accent hover:text-accent disabled:cursor-wait disabled:opacity-50 sm:text-[13px]"
                                >
                                    {q.label}
                                </button>
                            ))}
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-muted">
                        That's the full briefing —{" "}
                        <a
                            href="#contact"
                            className="link-sweep pb-0.5 font-medium text-accent"
                        >
                            ask the real one →
                        </a>
                    </p>
                )}
            </div>
        </div>
    );
};

export default AskMe;